import { VDBText } from "@components/atoms";
import Modal from "@components/atoms/Modal";
import VDBIcon, { IconsVariant } from "@components/icons/VDBIcon";
import React, { useEffect, useState } from "react";
import {
  DeviceEventEmitter,
  FlatList,
  ScrollView,
  StyleSheet,
  TouchableOpacity,
  TouchableWithoutFeedback,
} from "react-native";
import { useTheme } from "styled-components/native";
import styled, { DefaultTheme } from "styled-components/native";

import { SpaceType } from "@components/atoms/Spacer/types";
import { View } from "@gluestack-ui/themed";
import { useTypographyStore } from "@redux/slices/typographySlice";
import { useTranslation } from "react-i18next";
import ImageComponentView from "./ImageComponentView";
import { MatchStoneModelProps } from "./types";
import { category, getProductBorderColor, getProductLevelColor } from "./Utils";

const MatchStoneGalleryModel = ({
  isOpen = false,
  onClose,
  ReceivedMedia,
  OtherMedia,
  productType,
  itemDetail,
  isPrimary = false,
}: MatchStoneModelProps) => {
  const theme = useTheme();
  const typography = useTypographyStore();
  const { t } = useTranslation();
  const [selectedType, setSelectedType] = useState("image");
  const [activeIndex, setActiveIndex] = useState(0);
  const [secondActiveIndex, setSecondActiveIndex] = useState(0);
  const [positionIndex, setPositionIndex] = useState(0);

  let thumbSize = 40;

  let borderColor = getProductBorderColor(productType);

  let backgroundColor = getProductLevelColor(productType);

  const [updatedReceivedMedia, setUpdatedReceivedMedia] =
    useState(ReceivedMedia);

  const [otherMediaArray, setOtherMediaArray] = useState(OtherMedia?.media);

  useEffect(() => {
    if (ReceivedMedia?.media?.length === 0) {
      setUpdatedReceivedMedia([{ type: "image", link: "" }]);
    } else {
      setUpdatedReceivedMedia(ReceivedMedia);
    }
  }, [ReceivedMedia]);

  const extractTypes = (media: any) => {
    const categorizedMedia = {
      image: [],
      video: [],
      pdf: [],
    };
    if (media?.length === 0 || media === null) return categorizedMedia;

    media?.forEach((item) => {
      if (item?.type in categorizedMedia) {
        if (categorizedMedia?.[item?.type].length === 0) {
          categorizedMedia?.[item.type].push(item);
        }
      }
    });

    for (const key in categorizedMedia) {
      if (categorizedMedia?.[key].length === 0) {
        categorizedMedia?.[key].push({ type: key, link: "" });
      }
    }

    return categorizedMedia;
  };

  const mediaTypes = extractTypes(updatedReceivedMedia);
  const otherMediaTypes = extractTypes(otherMediaArray);

  useEffect(() => {
    if (OtherMedia?.media?.length === 0) {
      setOtherMediaArray([{ type: "image", link: "" }]);
    }
  }, [OtherMedia?.media]);

  const scrollToIndex = (type: string) => {
    setSelectedType(type);

    let index;
    if (type === "image") {
      index = 0;
    } else if (type === "video") {
      index = 1;
    } else {
      index = 2;
    }

    setPositionIndex(index);
    setActiveIndex(0);
    setSecondActiveIndex(0);
  };

  const renderThumb = ({ item, index }: any) => {
    return (
      <TouchableOpacity
        onPress={() => scrollToIndex(item.title)}
        activeOpacity={0.8}
      >
        <View
          style={[
            styles.thumb,
            {
              borderColor:
                positionIndex === index
                  ? (theme[borderColor as keyof DefaultTheme] as string)
                  : theme["core-03"],
              backgroundColor:
                positionIndex === index
                  ? (theme[backgroundColor as keyof DefaultTheme] as string)
                  : theme["core-03"],
              justifyContent: "center",
              alignContent: "center",
              alignItems: "center",
            },
            { width: thumbSize, height: thumbSize },
          ]}
        >
          <VDBIcon
            name={item.icon}
            size={24}
            color={theme["core-06"]}
            variant={IconsVariant.VDBCustom}
          />
        </View>
      </TouchableOpacity>
    );
  };

  return (
    <Modal
      headerTitle={t("findMatch.model.title.text")}
      isOpen={isOpen}
      onClose={onClose}
      paddingLeft={0}
      paddingRight={0}
      paddingBottom={0}
      paddingTop={0}
      enableToastMessage={true}
      size="lg"
      noPadding
    >
      <MainContainer>
        <TouchableWithoutFeedback
          onPress={() => DeviceEventEmitter.emit("SHARE_MENU_SWITCH")}
        >
          <ScrollView keyboardShouldPersistTaps={"always"}>
            <GalleryContainer>
              <Container>
                {!theme.isMobile && (
                  <LabelContainerView>
                    <VDBText color={theme["core-05"]} type={typography["H3-M"]}>
                      {" "}
                      {t("findMatch.yourChice.text")}
                    </VDBText>
                  </LabelContainerView>
                )}
                <LeftLabelContainer keyMargin={20}>
                  <ImageComponentView
                    ReceivedMedia={!isPrimary ? otherMediaTypes : mediaTypes}
                    initialIndex={activeIndex}
                    itemDetail={!isPrimary ? OtherMedia : itemDetail}
                    isSmallDeviceWidth={true}
                    resizeImage={true}
                    isOpen={isOpen}
                    modalText={"findMatch.yourChice.text"}
                    productType={productType}
                    fromMatchPair={true}
                    noImageBorderRadius={false}
                    selectedType={selectedType}
                    onClose={onClose}
                  />
                </LeftLabelContainer>
              </Container>

              <Container>
                {!theme.isMobile && (
                  <LabelContainerView>
                    <VDBText color={theme["core-05"]} type={typography["H3-M"]}>
                      {" "}
                      {t("findMatch.matchStone.text")}
                    </VDBText>
                  </LabelContainerView>
                )}
                <LeftLabelContainer keyMargin={20}>
                  <ImageComponentView
                    ReceivedMedia={!isPrimary ? mediaTypes : otherMediaTypes}
                    initialIndex={secondActiveIndex}
                    itemDetail={!isPrimary ? itemDetail : OtherMedia}
                    isSmallDeviceWidth={true}
                    resizeImage={true}
                    isOpen={isOpen}
                    productType={productType}
                    modalText={"findMatch.matchStone.text"}
                    fromMatchPair={true}
                    noImageBorderRadius={false}
                    selectedType={selectedType}
                    onClose={onClose}
                  />
                </LeftLabelContainer>
              </Container>
            </GalleryContainer>

            <BottomContainer softShadow="1">
              <FlatList
                data={category}
                renderItem={renderThumb}
                horizontal
                contentContainerStyle={styles.iconListContainer}
              />
            </BottomContainer>
          </ScrollView>
        </TouchableWithoutFeedback>
      </MainContainer>
    </Modal>
  );
};

const MainContainer = styled(View)`
  background-color: ${({ theme }) => theme["core-02"]};
`;

const LeftLabelContainer = styled(View)`
  flex-direction: row;
  align-items: center;
  align-self: center;
`;

const Container = styled.View`
  border-radius: 8px;
  border-width: ${({ theme }) => (theme.isMobile ? "1px" : 0)};
  border-color: ${({ theme }) => theme["core-06"]};
  margin-bottom: 10px;
  margin-left: 16px;
  margin-right: 16px;
  margin-top: 10px;
  background-color: ${({ theme }) => theme["core-02"]};
`;

const LabelContainerView = styled.View<any>`
  align-items: center;
  justify-content: center;
  margin-top: 10px;
  margin-bottom: 10px;
`;

const GalleryContainer = styled.View`
  flex-direction: ${({ theme }) => (theme.isMobile ? "column" : "row")};
  align-items: ${({ theme }) => (theme.isMobile ? "flex-start" : "center")};
  justify-content: ${({ theme }) => !theme.isMobile && "center"};
`;

const BottomContainer = styled.View<any>`
  flex: 1;
  flex-basis: auto;
  justify-content: center;
  align-items: center;
  padding: 10px;
  padding-top: ${SpaceType.Big}px;
  padding-bottom: ${SpaceType.ExtraLarge}px;
  background-color: white;
`;

const styles = StyleSheet.create({
  thumb: {
    borderRadius: 12,
    marginRight: 0,
    borderWidth: 1,
  },
  thumbnailListContainer: {
    paddingHorizontal: 10,
  },
  bottomFlatlist: {
    position: "absolute",
  },

  iconListContainer: {
    justifyContent: "center",
    alignItems: "center",
    gap: 24,
  },
  iconContainer: {
    marginHorizontal: 10,
  },
});

export default MatchStoneGalleryModel;
